/**
 * Shared formatting for the analytics components.
 *
 * Resolvers return unitless numbers — a Ratio gives 0.3712, not "37.1%" — so
 * deciding how a figure reads belongs here rather than in the query layer.
 */

/** Shown where a figure is unavailable rather than zero. */
export const UNAVAILABLE = '—';

const isMissing = value =>
  value === null || value === undefined || Number.isNaN(value);

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A bucket names a calendar day, but `new Date('2026-03-01')` reads it as UTC
 * midnight — which is the day before anywhere west of Greenwich. Building it
 * from its parts lands on local midnight, so the day survives the reader's
 * timezone. Anything else is left to `Date` to parse.
 *
 * @param {string|Date} at
 * @returns {Date}
 */
export function toDate(at) {
  if (at instanceof Date) return at;

  const parts = DATE_ONLY.exec(String(at));

  if (!parts) return new Date(at);

  return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
}

/**
 * @param {number|null} value
 * @param {{ locale?: string, format?: 'number'|'percent', precision?: number|null }} [options]
 * @returns {string} UNAVAILABLE when the value is missing; zero always formats as a figure
 */
export function formatValue(
  value,
  { locale = 'en', format = 'number', precision = null } = {}
) {
  if (isMissing(value)) return UNAVAILABLE;

  const percent = format === 'percent';
  const digits = precision ?? (percent ? 1 : 0);

  return new Intl.NumberFormat(locale, {
    style: percent ? 'percent' : 'decimal',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/**
 * Formats a timeseries bucket. Buckets arrive as ISO dates from jsonb, and the
 * grain decides how much of the date is worth showing — a monthly bucket
 * labelled with a day reads as a precision the data does not have.
 *
 * `short` drops the year, for axis ticks where the range is already established
 * by the caption and there is no room to repeat it under every bucket.
 *
 * @param {string|Date} at
 * @param {{ locale?: string, grain?: 'day'|'week'|'month', short?: boolean }} [options]
 * @returns {string}
 */
export function formatBucket(
  at,
  { locale = 'en', grain = 'day', short = false } = {}
) {
  const date = toDate(at);

  if (Number.isNaN(date.getTime())) return String(at);

  const year = short ? undefined : 'numeric';
  const options =
    grain === 'month'
      ? { year: 'numeric', month: 'short' }
      : { year, month: 'short', day: 'numeric' };

  return new Intl.DateTimeFormat(locale, options).format(date);
}

/**
 * True when a series carries time buckets rather than categorical ones.
 *
 * @param {Array<object>} rows
 * @returns {boolean}
 */
export function isTimeseries(rows) {
  return (
    Array.isArray(rows) && rows.length > 0 && rows[0] != null && 'at' in rows[0]
  );
}
